/**
 * Email Tracking Badges Component
 * Shows delivery/engagement status for emails
 * Works with both crm_email_inbox and supplier_email_threads
 */

'use client';

import React from 'react';
import { Badge } from '@/shared/components/ui/badge';
import { Eye, MousePointerClick, Check, X, AlertCircle, Loader } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export interface EmailTrackingData {
  email_delivered?: boolean;
  email_delivered_at?: string | null;
  email_opened?: boolean;
  email_opened_at?: string | null;
  email_open_count?: number;
  email_clicked?: boolean;
  email_clicked_at?: string | null;
  email_click_count?: number;
  email_bounced?: boolean;
  email_bounced_at?: string | null;
  email_bounce_reason?: string | null;
}

interface EmailTrackingBadgesProps {
  data: EmailTrackingData;
  variant?: 'compact' | 'detailed';
  className?: string;
}

export function EmailTrackingBadges({ 
  data, 
  variant = 'compact',
  className = '' 
}: EmailTrackingBadgesProps) {
  const {
    email_delivered,
    email_delivered_at,
    email_opened,
    email_opened_at,
    email_open_count = 0,
    email_clicked,
    email_clicked_at,
    email_click_count = 0,
    email_bounced,
    email_bounced_at,
    email_bounce_reason,
  } = data;

  // # Check if tracking is applicable
  // If ALL tracking fields are null/undefined, tracking isn't set up - don't show anything
  // If any field is false/true or has a timestamp, tracking exists (even if not complete)
  const hasAnyTrackingValue = 
    email_bounced === true || email_bounced === false ||
    email_delivered === true || email_delivered === false ||
    email_opened === true || email_opened === false ||
    email_clicked === true || email_clicked === false ||
    email_delivered_at !== null ||
    email_opened_at !== null ||
    email_clicked_at !== null ||
    email_bounced_at !== null ||
    email_bounce_reason !== null;

  // If no tracking data exists at all (all null/undefined), don't render anything
  // This handles inbound emails where tracking fields are never set
  if (!hasAnyTrackingValue) {
    return null;
  }

  // Determine overall status
  const status: 'bounced' | 'clicked' | 'opened' | 'delivered' | 'sending' = 
    email_bounced ? 'bounced' :
    email_clicked ? 'clicked' :
    email_opened ? 'opened' :
    email_delivered ? 'delivered' :
    'sending';

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {email_bounced && (
          <span title={`Bounced: ${email_bounce_reason || 'Unknown reason'}`}>
            <Badge variant="destructive" className="h-5 px-1.5 text-[10px] font-normal">
              <X className="h-2.5 w-2.5" />
            </Badge>
          </span>
        )}
        
        {!email_bounced && email_clicked && (
          <span title={`Clicked ${email_click_count}x ${email_clicked_at ? '- ' + formatDistanceToNow(new Date(email_clicked_at), { addSuffix: true }) : ''}`}>
            <Badge variant="default" className="h-5 px-1.5 text-[10px] font-normal bg-purple-500 hover:bg-purple-600">
              <MousePointerClick className="h-2.5 w-2.5" />
              {email_click_count > 1 && <span className="ml-0.5">{email_click_count}</span>}
            </Badge>
          </span>
        )}
        
        {!email_bounced && !email_clicked && email_opened && (
          <span title={`Opened ${email_open_count}x ${email_opened_at ? '- ' + formatDistanceToNow(new Date(email_opened_at), { addSuffix: true }) : ''}`}>
            <Badge variant="default" className="h-5 px-1.5 text-[10px] font-normal bg-blue-500 hover:bg-blue-600">
              <Eye className="h-2.5 w-2.5" />
              {email_open_count > 1 && <span className="ml-0.5">{email_open_count}</span>}
            </Badge>
          </span>
        )}
        
        {!email_bounced && !email_clicked && !email_opened && email_delivered && (
          <span title={`Delivered ${email_delivered_at ? formatDistanceToNow(new Date(email_delivered_at), { addSuffix: true }) : ''}`}>
            <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
              <Check className="h-2.5 w-2.5" />
            </Badge>
          </span>
        )}
        
        {!email_bounced && !email_delivered && !email_opened && !email_clicked && (
          <span title="Sending...">
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
              <Loader className="h-2.5 w-2.5 animate-spin" />
            </Badge>
          </span>
        )}
      </div>
    );
  }

  // Detailed variant
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {email_bounced && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <X className="h-4 w-4" />
          <div>
            <span className="font-medium">Bounced</span>
            {email_bounced_at && (
              <span className="text-xs text-gray-500 ml-2">
                {formatDistanceToNow(new Date(email_bounced_at), { addSuffix: true })}
              </span>
            )}
            {email_bounce_reason && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {email_bounce_reason}
              </p>
            )}
          </div>
        </div>
      )}
      
      {!email_bounced && (
        <>
          {email_delivered && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
              <span className="font-medium">Delivered</span>
              {email_delivered_at && (
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(email_delivered_at), { addSuffix: true })}
                </span>
              )}
            </div>
          )}
          
          {email_opened && (
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <Eye className="h-4 w-4" />
              <span className="font-medium">
                Opened {email_open_count > 1 && `(${email_open_count}x)`}
              </span>
              {email_opened_at && (
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(email_opened_at), { addSuffix: true })}
                </span>
              )}
            </div>
          )}
          
          {email_clicked && (
            <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
              <MousePointerClick className="h-4 w-4" />
              <span className="font-medium">
                Clicked {email_click_count > 1 && `(${email_click_count}x)`}
              </span>
              {email_clicked_at && (
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(email_clicked_at), { addSuffix: true })}
                </span>
              )}
            </div>
          )}
          
          {!email_delivered && !email_opened && !email_clicked && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader className="h-4 w-4 animate-spin" />
              <span className="font-medium">Sending...</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Simple status icon only (for very compact displays)
 */
export function EmailTrackingIcon({ data }: { data: EmailTrackingData }) {
  const {
    email_bounced,
    email_clicked,
    email_opened,
    email_delivered,
  } = data;

  if (email_bounced) {
    return <span title="Bounced"><X className="h-3.5 w-3.5 text-red-500" /></span>;
  }
  
  if (email_clicked) {
    return <span title="Clicked"><MousePointerClick className="h-3.5 w-3.5 text-purple-500" /></span>;
  }
  
  if (email_opened) {
    return <span title="Opened"><Eye className="h-3.5 w-3.5 text-blue-500" /></span>;
  }
  
  if (email_delivered) {
    return <span title="Delivered"><Check className="h-3.5 w-3.5 text-green-500" /></span>;
  }
  
  return <span title="Sending"><Loader className="h-3.5 w-3.5 text-gray-400 animate-spin" /></span>;
}

